import React from 'react';
import { Box, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { BRAND_COLORS, alpha } from '../../styles/theme';
import type { AnalyticsIssue } from '../../types/analytics';
import InsightFeedbackButtons from './InsightFeedbackButtons';

interface IssueCardProps {
  issue: AnalyticsIssue;
  weekStart: string;
  feedbackWritable: boolean;
}

const IssueCard: React.FC<IssueCardProps> = ({ issue, weekStart, feedbackWritable }) => {
  const severityColor =
    issue.severity === 'critical'
      ? '#d32f2f'
      : issue.severity === 'warning'
        ? '#ed6c02'
        : BRAND_COLORS.forestGreen.main;

  const Icon =
    issue.severity === 'critical'
      ? ErrorOutlineIcon
      : issue.severity === 'warning'
        ? WarningAmberIcon
        : InfoOutlinedIcon;

  const iconColor =
    issue.severity === 'critical'
      ? 'error.main'
      : issue.severity === 'warning'
        ? 'warning.main'
        : 'info.main';

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: alpha(severityColor, 0.06),
        borderLeft: '3px solid',
        borderColor: iconColor,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Icon sx={{ fontSize: 16, color: iconColor }} />
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>
          {issue.title}
        </Typography>
        <InsightFeedbackButtons
          insightKey={issue.insight_key}
          weekStart={weekStart}
          writable={feedbackWritable}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {issue.description}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: BRAND_COLORS.forestGreen.main, fontWeight: 500 }}
        display="block"
      >
        Fix: {issue.suggestion}
      </Typography>
      {issue.goal_name && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ fontStyle: 'italic', mt: 0.25 }}
        >
          Goal: {issue.goal_name}
        </Typography>
      )}
    </Box>
  );
};

export default IssueCard;
